import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, LinearProgress,
  Chip, Skeleton, Alert,
} from '@mui/material';
import { CheckCircle, Lock, MenuBook, ArrowForward } from '@mui/icons-material';
import { academyApi, AcademyModule, AcademyModulesResponse } from '../../services/academyApi';
import { MODULES_CONTENT } from './content/modules';
import ModuleViewer from './ModuleViewer';

const CATEGORY_COLORS: Record<string, string> = {
  'Grundlagen': '#2196F3',
  'Indikatoren': '#FF9800',
  'Strategien': '#9C27B0',
  'Fortgeschritten': '#4CAF50',
};

const AcademyPage: React.FC = () => {
  const [data, setData] = useState<AcademyModulesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedModule, setSelectedModule] = useState<string | null>(null);

  const fetchModules = () => {
    setLoading(true);
    academyApi.getModules()
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchModules(); }, []);

  const handleComplete = async (moduleId: string) => {
    setCompleting(moduleId);
    setError(null);
    try {
      await academyApi.completeModule(moduleId);
      fetchModules();
    } catch (e: any) {
      setError(e.message || 'Fehler beim Abschließen');
    } finally {
      setCompleting(null);
    }
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="rectangular" height={8} sx={{ my: 2, borderRadius: 4 }} />
        <Grid container spacing={2}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  const modules = data?.modules ?? [];
  const completedCount = data?.completed_count ?? 0;
  const totalCount = data?.total_count ?? 12;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  // Group by category
  const categories = Array.from(new Set(modules.map((m) => m.category)));

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Academy</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Lerne Trading von Grund auf — 12 Module von Anfänger bis Fortgeschritten.
      </Typography>

      {/* Progress bar */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" fontWeight={600}>Dein Fortschritt</Typography>
          <Typography variant="body2" color="text.secondary">
            {completedCount} / {totalCount} Module
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{ height: 10, borderRadius: 5 }}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Modules by category */}
      {categories.map((category) => (
        <Box key={category} sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box
              sx={{
                width: 4, height: 24, borderRadius: 2,
                bgcolor: CATEGORY_COLORS[category] || 'primary.main',
              }}
            />
            <Typography variant="h6" fontWeight={600}>{category}</Typography>
          </Box>

          <Grid container spacing={2}>
            {modules
              .filter((m) => m.category === category)
              .map((module) => (
                <Grid item xs={12} sm={6} md={4} key={module.id}>
                  <ModuleCard
                    module={module}
                    onClick={() => {
                      if (module.unlocked) setSelectedModule(module.id);
                    }}
                    completing={completing === module.id}
                  />
                </Grid>
              ))}
          </Grid>
        </Box>
      ))}

      {/* Module Viewer Dialog */}
      <ModuleViewer
        module={selectedModule ? (MODULES_CONTENT[selectedModule] ?? null) : null}
        open={selectedModule !== null}
        onClose={() => setSelectedModule(null)}
        onComplete={(moduleId) => {
          handleComplete(moduleId);
          setSelectedModule(null);
        }}
        isCompleted={
          selectedModule
            ? (modules.find((m) => m.id === selectedModule)?.completed ?? false)
            : false
        }
        completing={completing !== null}
      />
    </Box>
  );
};

interface ModuleCardProps {
  module: AcademyModule;
  onClick: () => void;
  completing: boolean;
}

const ModuleCard: React.FC<ModuleCardProps> = ({ module, onClick, completing }) => {
  const isCompleted = module.completed;
  const isLocked = !module.unlocked;
  const isNext = module.unlocked && !module.completed;

  return (
    <Card
      sx={{
        borderRadius: 3, height: '100%',
        opacity: isLocked ? 0.5 : 1,
        border: isNext ? 2 : 1,
        borderColor: isNext ? 'primary.main' : isCompleted ? 'success.main' : 'divider',
        cursor: isLocked ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s',
        '&:hover': isLocked ? {} : { transform: 'translateY(-2px)', boxShadow: 3 },
      }}
      onClick={() => {
        if (!isLocked && !completing) {
          onClick();
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ fontSize: 28 }}>
            {isCompleted ? (
              <CheckCircle color="success" sx={{ fontSize: 28 }} />
            ) : isLocked ? (
              <Lock color="disabled" sx={{ fontSize: 28 }} />
            ) : (
              <MenuBook color="primary" sx={{ fontSize: 28 }} />
            )}
          </Box>
          <Chip
            label={`#${module.order}`}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
        </Box>

        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {module.title}
        </Typography>

        <Typography variant="caption" color="text.secondary">
          {module.category}
        </Typography>

        {isCompleted && (
          <Typography variant="caption" display="block" color="success.main" sx={{ mt: 1 }}>
            Abgeschlossen ✓
          </Typography>
        )}

        {isNext && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, color: 'primary.main' }}>
            <Typography variant="caption" fontWeight={600}>
              {completing ? 'Wird abgeschlossen...' : 'Klick zum Lesen'}
            </Typography>
            <ArrowForward sx={{ fontSize: 14 }} />
          </Box>
        )}

        {isLocked && (
          <Typography variant="caption" display="block" color="text.disabled" sx={{ mt: 1 }}>
            Vorheriges Modul zuerst
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default AcademyPage;
